import React, { useState, useEffect } from 'react';
import { X, Upload, Calendar, User, Building, MapPin, DollarSign, Users, Clock, FileText, AlertCircle } from 'lucide-react';
import DragDropUploader from './shared/DragDropUploader';

const EnhancedJobCreateModal = ({ isOpen, onClose, onSuccess, editingJob }) => {
  const [formData, setFormData] = useState({
    // Basic Job Information
    title: '',
    description: '',
    priority: 'normal',
    
    // Client Information
    client: '',
    client_contact: '',
    account_manager: '',
    
    // Job Dates
    end_date: '',
    
    // Job Details
    no_of_openings: 1,
    
    // Department and Organization
    department: '',
    client_cost_center: '',
    workday_supervisory_org: '',
    
    // Compensation
    bill_rate: '',
    pay_unit: 'hourly',
    salary_range: {
      min: '',
      max: '',
      currency: 'USD'
    },
    
    // Skills and Requirements
    required_skills: [],
    preferred_skills: [],
    experience_level: 'mid',
    
    // Work Arrangement
    travel: '0',
    relocation_allowed: 'yes',
    location_requirements: 'onsite',
    visa_sponsorship: 'yes',
    
    // Assessment
    assessment: 'N/A',
    
    // Location Information
    location: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    
    // Employment Type
    employment_type: 'full-time',
    
    // Team Assignment
    assigned_recruiters: [],
    hiring_manager: '',
    
    // File Attachments
    job_description_document: null
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showUploader, setShowUploader] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editingJob) {
      setFormData({
        title: editingJob.title || '',
        description: editingJob.description || '',
        priority: editingJob.priority || 'normal',
        client: editingJob.client || editingJob.client_name || '',
        client_contact: editingJob.client_contact || '',
        account_manager: editingJob.account_manager || '',
        end_date: editingJob.end_date ? editingJob.end_date.split('T')[0] : '',
        no_of_openings: editingJob.no_of_openings || 1,
        department: editingJob.department || '',
        client_cost_center: editingJob.client_cost_center || '',
        workday_supervisory_org: editingJob.workday_supervisory_org || '',
        bill_rate: editingJob.bill_rate || '',
        pay_unit: editingJob.pay_unit || 'hourly',
        salary_range: editingJob.salary_range || { min: '', max: '', currency: 'USD' },
        required_skills: editingJob.required_skills || [],
        preferred_skills: editingJob.preferred_skills || [],
        experience_level: editingJob.experience_level || 'mid',
        travel: editingJob.travel || '0',
        relocation_allowed: editingJob.relocation_allowed || 'yes',
        location_requirements: editingJob.location_requirements || 'onsite',
        visa_sponsorship: editingJob.visa_sponsorship || 'yes',
        assessment: editingJob.assessment || 'N/A',
        location: editingJob.location || '',
        address_line1: editingJob.address_line1 || '',
        address_line2: editingJob.address_line2 || '',
        city: editingJob.city || '',
        state: editingJob.state || '',
        zip_code: editingJob.zip_code || editingJob.postal_code || '',
        employment_type: editingJob.employment_type || 'full-time',
        assigned_recruiters: editingJob.assigned_recruiters || [],
        hiring_manager: editingJob.hiring_manager || '',
        job_description_document: null
      });
    }
  }, [editingJob]);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchContacts();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setClients(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      // This would be implemented when contacts API is available
      setContacts([
        { id: 'contact1', name: 'John Smith', email: 'john@example.com', role: 'Hiring Manager' },
        { id: 'contact2', name: 'Jane Doe', email: 'jane@example.com', role: 'Recruiter' },
        { id: 'contact3', name: 'Mike Johnson', email: 'mike@example.com', role: 'Account Manager' }
      ]);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation with tab mapping
    const fieldToTab = {
      title: 'basic',
      client: 'client',
      client_contact: 'client',
      account_manager: 'client',
      end_date: 'basic',
      department: 'client',
      location: 'location',
      hiring_manager: 'client'
    };

    if (!formData.title) newErrors.title = 'Position title is required';
    if (!formData.client) newErrors.client = 'Client is required';
    if (!formData.client_contact) newErrors.client_contact = 'Client contact is required';
    if (!formData.account_manager) newErrors.account_manager = 'Account manager is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.hiring_manager) newErrors.hiring_manager = 'Hiring manager is required';

    // Title length validation
    if (formData.title && formData.title.length > 40) {
      newErrors.title = 'Position title must not exceed 40 characters';
    }

    // Number validation
    if (formData.no_of_openings && formData.no_of_openings < 1) {
      newErrors.no_of_openings = 'Number of openings must be at least 1';
    }

    // Date validation
    if (formData.end_date && new Date(formData.end_date) <= new Date()) {
      newErrors.end_date = 'End date must be in the future';
    }

    setErrors(newErrors);
    
    // Navigate to first tab with errors
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorTab = fieldToTab[firstErrorField];
      if (errorTab) {
        setActiveTab(errorTab);
        // Small delay to allow tab to render before showing alert
        setTimeout(() => {
          alert(`Please fill in required fields. First missing: ${newErrors[firstErrorField]}`);
        }, 100);
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        required_skills: formData.required_skills,
        preferred_skills: formData.preferred_skills,
        assigned_recruiters: formData.assigned_recruiters,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        bill_rate: formData.bill_rate ? parseFloat(formData.bill_rate) : null,
        no_of_openings: parseInt(formData.no_of_openings) || 1
      };

      const isEditing = editingJob && editingJob.id;
      const url = isEditing 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${editingJob.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/jobs`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        onSuccess && onSuccess(result);
        onClose();
        
        // Reset form
        setFormData({
          title: '', description: '', priority: 'normal', client: '', client_contact: '',
          account_manager: '', end_date: '', no_of_openings: 1, department: '',
          client_cost_center: '', workday_supervisory_org: '', bill_rate: '',
          pay_unit: 'hourly', salary_range: { min: '', max: '', currency: 'USD' },
          required_skills: [], preferred_skills: [], experience_level: 'mid',
          travel: '0', relocation_allowed: 'yes', location_requirements: 'onsite',
          visa_sponsorship: 'yes', assessment: 'N/A', location: '',
          address_line1: '', address_line2: '', city: '', state: '', zip_code: '',
          employment_type: 'full-time', assigned_recruiters: [], hiring_manager: '',
          job_description_document: null
        });
        setErrors({});
      } else {
        const errorData = await response.json();
        // Handle different error response formats
        let errorMessage = 'Unknown error';
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          // Handle FastAPI ValidationError format
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle array of validation errors
            errorMessage = errorData.detail.map(err => 
              `${err.loc?.join(' -> ') || 'Field'}: ${err.msg}`
            ).join('\n');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        alert(`Error creating job:\n${errorMessage}`);
      }
    } catch (error) {
      console.error('Error creating job:', error);
      alert(`Error creating job: ${error.message || 'Please check your connection and try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillsChange = (field, value) => {
    const skills = value.split(',').map(skill => skill.trim()).filter(skill => skill);
    setFormData({ ...formData, [field]: skills });
  };

  const handleRecruitersChange = (value) => {
    const recruiters = value.split(',').map(recruiter => recruiter.trim()).filter(recruiter => recruiter);
    setFormData({ ...formData, assigned_recruiters: recruiters });
  };

  const handleJobDescriptionParsed = (parsedData) => {
    // Parse salary range if it's a string like "$80,000 - $120,000"
    let salaryMin = '';
    let salaryMax = '';
    if (parsedData.salary_range && typeof parsedData.salary_range === 'string') {
      const salaryMatch = parsedData.salary_range.match(/(\d[\d,]*)\s*-\s*(\d[\d,]*)/);
      if (salaryMatch) {
        salaryMin = salaryMatch[1].replace(/,/g, '');
        salaryMax = salaryMatch[2].replace(/,/g, '');
      }
    }

    // Update form data with parsed values
    setFormData(prev => ({
      ...prev,
      title: parsedData.job_title || prev.title,
      description: parsedData.description || parsedData.responsibilities || prev.description,
      department: parsedData.department || prev.department,
      location: parsedData.location || prev.location,
      employment_type: parsedData.employment_type?.toLowerCase().replace(' ', '-') || prev.employment_type,
      experience_level: parsedData.experience_level?.toLowerCase() || prev.experience_level,
      required_skills: parsedData.required_skills || prev.required_skills,
      preferred_skills: parsedData.preferred_skills || prev.preferred_skills,
      salary_range: {
        ...prev.salary_range,
        min: salaryMin || prev.salary_range.min,
        max: salaryMax || prev.salary_range.max
      }
    }));

    // Close uploader and show success message
    setShowUploader(false);
    alert('Job description parsed successfully! Please review and complete remaining fields.');
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'client', label: 'Client & Team', icon: Building },
    { id: 'compensation', label: 'Compensation', icon: DollarSign },
    { id: 'requirements', label: 'Requirements', icon: Users },
    { id: 'location', label: 'Location & Work', icon: MapPin }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position Title * <span className="text-xs text-gray-500">(Max 40 characters)</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                maxLength="40"
                placeholder="e.g., Senior Software Engineer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{errors.title && <span className="text-red-500">{errors.title}</span>}</span>
                <span>{formData.title.length}/40</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.end_date ? 'border-red-300' : 'border-gray-300'
                }`}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.end_date && <span className="text-xs text-red-500">{errors.end_date}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Openings *</label>
              <input
                type="number"
                value={formData.no_of_openings}
                onChange={(e) => setFormData({ ...formData, no_of_openings: parseInt(e.target.value) || 1 })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.no_of_openings ? 'border-red-300' : 'border-gray-300'
                }`}
                min="1"
              />
              {errors.no_of_openings && <span className="text-xs text-red-500">{errors.no_of_openings}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Detailed job description..."
              />
            </div>
          </div>
        );

      case 'client':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client * <span className="text-xs text-gray-500">(Max 20 characters)</span>
              </label>
              <select
                value={formData.client}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.client ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.company_name}>
                    {client.company_name}
                  </option>
                ))}
              </select>
              {errors.client && <span className="text-xs text-red-500">{errors.client}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Contact * <span className="text-xs text-gray-500">(Max 20 characters)</span>
              </label>
              <select
                value={formData.client_contact}
                onChange={(e) => setFormData({ ...formData, client_contact: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.client_contact ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Contact</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.name}>
                    {contact.name} - {contact.role}
                  </option>
                ))}
              </select>
              {errors.client_contact && <span className="text-xs text-red-500">{errors.client_contact}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Manager * <span className="text-xs text-gray-500">(Max 20 characters)</span>
              </label>
              <select
                value={formData.account_manager}
                onChange={(e) => setFormData({ ...formData, account_manager: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.account_manager ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Account Manager</option>
                {contacts.filter(c => c.role === 'Account Manager').map((contact) => (
                  <option key={contact.id} value={contact.name}>
                    {contact.name}
                  </option>
                ))}
              </select>
              {errors.account_manager && <span className="text-xs text-red-500">{errors.account_manager}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.department ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Engineering, Sales, Marketing"
              />
              {errors.department && <span className="text-xs text-red-500">{errors.department}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Cost Center</label>
              <select
                value={formData.client_cost_center}
                onChange={(e) => setFormData({ ...formData, client_cost_center: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Cost Center</option>
                <option value="DT/IT">DT/IT</option>
                <option value="Managed Cloud Services">Managed Cloud Services</option>
                <option value="ERP Services">ERP Services</option>
                <option value="Hybrid Cloud Delivery">Hybrid Cloud Delivery</option>
                <option value="AI/ML Juno Labs">AI/ML Juno Labs</option>
                <option value="Field Services">Field Services</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager *</label>
              <select
                value={formData.hiring_manager}
                onChange={(e) => setFormData({ ...formData, hiring_manager: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.hiring_manager ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Hiring Manager</option>
                {contacts.filter(c => c.role === 'Hiring Manager').map((contact) => (
                  <option key={contact.id} value={contact.name}>
                    {contact.name}
                  </option>
                ))}
              </select>
              {errors.hiring_manager && <span className="text-xs text-red-500">{errors.hiring_manager}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned Recruiters <span className="text-xs text-gray-500">(Comma-separated names)</span>
              </label>
              <input
                type="text"
                value={formData.assigned_recruiters.join(', ')}
                onChange={(e) => handleRecruitersChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Smith, Jane Doe"
              />
            </div>
          </div>
        );

      case 'compensation':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate</label>
              <input
                type="number"
                step="0.01"
                value={formData.bill_rate}
                onChange={(e) => setFormData({ ...formData, bill_rate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="150.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Unit *</label>
              <select
                value={formData.pay_unit}
                onChange={(e) => setFormData({ ...formData, pay_unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label>
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="number"
                  value={formData.salary_range.min}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_range: { ...formData.salary_range, min: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Min"
                />
                <input
                  type="number"
                  value={formData.salary_range.max}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_range: { ...formData.salary_range, max: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max"
                />
                <select
                  value={formData.salary_range.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    salary_range: { ...formData.salary_range, currency: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select
                value={formData.employment_type}
                onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="internship">Internship</option>
              </select>
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.required_skills.join(', ')}
                onChange={(e) => handleSkillsChange('required_skills', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Python, React, Node.js"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Skills <span className="text-xs text-gray-500">(Comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.preferred_skills.join(', ')}
                onChange={(e) => handleSkillsChange('preferred_skills', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AWS, Docker, Kubernetes"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level</label>
              <select
                value={formData.experience_level}
                onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="entry">Entry Level</option>
                <option value="mid">Mid Level</option>
                <option value="senior">Senior Level</option>
                <option value="lead">Lead Level</option>
                <option value="executive">Executive Level</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Travel Requirements</label>
              <select
                value={formData.travel}
                onChange={(e) => setFormData({ ...formData, travel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">0% (No Travel)</option>
                <option value="25%">25%</option>
                <option value="50%">50%</option>
                <option value="75%">75%</option>
                <option value="100%">100%</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visa Sponsorship Available</label>
              <select
                value={formData.visa_sponsorship}
                onChange={(e) => setFormData({ ...formData, visa_sponsorship: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assessment</label>
              <input
                type="text"
                value={formData.assessment}
                onChange={(e) => setFormData({ ...formData, assessment: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="N/A or specific assessment name"
              />
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.location ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Dallas, TX"
              />
              {errors.location && <span className="text-xs text-red-500">{errors.location}</span>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
              <input
                type="text"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Apartment, suite, etc."
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Dallas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="TX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="75201"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location Requirements</label>
              <select
                value={formData.location_requirements}
                onChange={(e) => setFormData({ ...formData, location_requirements: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Relocation Allowed</label>
              <select
                value={formData.relocation_allowed}
                onChange={(e) => setFormData({ ...formData, relocation_allowed: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingJob ? 'Edit Job Requisition' : 'Create New Job Requisition'}
              </h2>
              {!editingJob && (
                <button
                  type="button"
                  onClick={() => setShowUploader(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <Upload className="w-4 h-4" />
                  Upload JD
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-6 mt-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
            {renderTabContent()}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              * Required fields
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading 
                  ? (editingJob ? 'Updating...' : 'Creating...') 
                  : (editingJob ? 'Update Job Requisition' : 'Create Job Requisition')
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Drag and Drop Uploader Modal */}
      {showUploader && (
        <DragDropUploader
          uploadType="job_description"
          onFileParsed={handleJobDescriptionParsed}
          onClose={() => setShowUploader(false)}
        />
      )}
    </div>
  );
};

export default EnhancedJobCreateModal;